import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LOGO_URL } from '../constants';

const Header = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const servicesItems = [
    { name: 'ALL', path: '/services' },
    { name: 'HASSLE-FREE MOVE-INS', path: '/services/hassle-free-move-ins' },
    { name: 'FLATMATE MATCHING', path: '/services/flatmate-matching' },
    { name: 'MAINTENANCE RESOLUTION', path: '/services/maintenance-resolution' },
    { name: 'AMENITIES PACKAGE', path: '/services/amenities-package' },
    { name: 'FLEXIBLE LEASING OPTIONS', path: '/services/flexible-leasing-options' },
    { name: 'VETTED PROPERTIES', path: '/services/vetted-properties' },
    { name: 'NEIGHBORHOOD GUIDANCE', path: '/services/neighborhood-guidance' },
    { name: 'PAPERWORK ASSISTANCE', path: '/services/paperwork-assistance' },
    { name: '24/7 SUPPORT', path: '/services/24-7-support' },
    { name: 'COMMUNITY EVENTS', path: '/services/community-events' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // For exact matches like /about, /contact, /for-owners
    if (location.pathname === path) {
      return true;
    }
    // For paths that should match sub-routes (like /services)
    return location.pathname.startsWith(path + '/');
  };

  const isServicesActive = () => {
    return location.pathname.startsWith('/services');
  };

  return (
    <header className="bg-white w-full shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        {/* Main Header Row */}
        <div className="flex items-center justify-between">
          {/* Logo - Always on left */}
          <Link to="/" className="inline-flex flex-col hover:opacity-80 transition-opacity items-center gap-0">
            <img src={LOGO_URL} alt="FURNIFLEX" className="h-14 md:h-16 w-auto object-contain block" />
            <span className="text-xs font-medium text-gray-600 tracking-wide text-center">your space, your way</span>
          </Link>
          
          {/* Hamburger Menu Button - Mobile Only */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden p-2 text-gray-800 hover:text-furniflex-teal transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>

          {/* Desktop Navigation - Hidden on Mobile */}
          <nav className="hidden md:flex items-center gap-4 lg:gap-8">
          <Link 
            to="/" 
            className={`no-underline text-sm font-medium uppercase transition-colors ${
              isActive('/') ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
            }`}
          >
            HOME
          </Link>
          <Link 
            to="/about" 
            className={`no-underline text-sm font-medium uppercase transition-colors ${
              isActive('/about') ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
            }`}
          >
            ABOUT
          </Link>
          <Link 
            to="/for-owners" 
            className={`no-underline text-sm font-medium uppercase transition-colors ${
              isActive('/for-owners') ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
            }`}
          >
            FOR OWNERS
          </Link>
          <Link 
            to="/for-tenants" 
            className={`no-underline text-sm font-medium uppercase transition-colors ${
              isActive('/for-tenants') ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
            }`}
          >
            FOR TENANTS
          </Link>
          <div 
            className="relative inline-block"
            onMouseEnter={() => setIsServicesOpen(true)}
            onMouseLeave={() => setIsServicesOpen(false)}
          >
            <button 
              className={`bg-transparent border-none cursor-pointer p-0 text-sm font-medium uppercase transition-colors ${
                isServicesActive() ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
              }`}
              onClick={toggleServices}
            >
              SERVICES
            </button>
            {isServicesOpen && (
              <div 
                className="absolute top-full left-1/2 -translate-x-1/2 bg-white border border-gray-800 rounded w-[280px] max-w-[calc(100vw-2rem)] mt-1 shadow-lg z-50 overflow-y-auto max-h-[80vh]"
              >
                {servicesItems.map((item, index) => {
                  const isItemActive = location.pathname === item.path || 
                    (item.path === '/services' && location.pathname === '/services') ||
                    (item.path !== '/services' && location.pathname.startsWith(item.path));
                  return (
                    <Link 
                      key={index} 
                      to={item.path}
                      onClick={() => setIsServicesOpen(false)}
                      className={`block px-4 py-3 no-underline text-sm transition-colors border-b border-gray-100 last:border-b-0 whitespace-nowrap ${
                        isItemActive 
                          ? 'bg-gray-50 text-furniflex-teal font-semibold' 
                          : 'text-gray-800 hover:bg-gray-50 hover:text-furniflex-teal'
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          <Link 
            to="/contact" 
            className={`no-underline text-sm font-medium uppercase transition-colors ${
              isActive('/contact') ? 'text-furniflex-teal font-semibold' : 'text-gray-800 hover:text-furniflex-teal'
            }`}
          >
            CONTACT
          </Link>
          </nav>
        </div>

        {/* Mobile Menu - Shows when hamburger is clicked */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <div className="flex flex-col space-y-2">
              <Link 
                to="/" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium uppercase transition-colors ${
                  isActive('/') 
                    ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                HOME
              </Link>
              <Link 
                to="/about" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium uppercase transition-colors ${
                  isActive('/about') 
                    ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                ABOUT
              </Link>
              <Link 
                to="/for-owners" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium uppercase transition-colors ${
                  isActive('/for-owners') 
                    ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                FOR OWNERS
              </Link>
              <Link 
                to="/for-tenants" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium uppercase transition-colors ${
                  isActive('/for-tenants') 
                    ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                FOR TENANTS
              </Link>
              
              {/* Services Dropdown in Mobile */}
              <div className="px-4 py-3">
                <button
                  onClick={toggleServices}
                  className={`w-full text-left text-sm font-medium uppercase transition-colors ${
                    isServicesActive() 
                      ? 'text-furniflex-teal font-semibold' 
                      : 'text-gray-800'
                  }`}
                >
                  SERVICES {isServicesOpen ? '▼' : '▶'}
                </button>
                {isServicesOpen && (
                  <div className="mt-2 ml-4 space-y-1 border-l-2 border-gray-200 pl-4">
                    {servicesItems.map((item, index) => {
                      const isItemActive = location.pathname === item.path || 
                        (item.path === '/services' && location.pathname === '/services') ||
                        (item.path !== '/services' && location.pathname.startsWith(item.path));
                      return (
                        <Link 
                          key={index} 
                          to={item.path}
                          onClick={() => {
                            setIsServicesOpen(false);
                            setIsMobileMenuOpen(false);
                          }}
                          className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                            isItemActive 
                              ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                              : 'text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          {item.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              <Link 
                to="/contact" 
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-lg text-sm font-medium uppercase transition-colors ${
                  isActive('/contact') 
                    ? 'bg-furniflex-teal/10 text-furniflex-teal font-semibold' 
                    : 'text-gray-800 hover:bg-gray-100'
                }`}
              >
                CONTACT
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
